import { useState, useRef, useEffect } from 'react';

interface VideoPlayerProps {
  /** MP4 video URL - if empty, shows placeholder */
  src?: string;
  /** Poster image URL */
  poster?: string;
  /** Title shown in placeholder / overlay */
  title: string;
  /** Subtitle / description */
  subtitle?: string;
  /** Duration label e.g. "约20分钟" */
  duration?: string;
  /** Extra class for the outer wrapper */
  className?: string;
  /** Height class, default h-64 */
  heightClass?: string;
  /** Dark theme (default) or light */
  theme?: 'dark' | 'light';
}

export default function VideoPlayer({
  src,
  poster,
  title,
  subtitle,
  duration,
  className = '',
  heightClass = 'h-64',
  theme = 'dark',
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [started, setStarted] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDark = theme === 'dark';

  const formatTime = (s: number) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (!src) return;
    const v = videoRef.current;
    if (!v) return;
    if (!started) setStarted(true);
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    setProgress(v.duration ? (v.currentTime / v.duration) * 100 : 0);
  };

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    setTotalDuration(v.duration);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    v.currentTime = ratio * v.duration;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) videoRef.current.volume = val;
    setMuted(val === 0);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  const showControlsTemporarily = () => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (playing) setShowControls(false);
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  // No src — show placeholder
  if (!src) {
    return (
      <div
        className={`relative rounded-2xl overflow-hidden ${heightClass} ${className} flex items-center justify-center`}
        style={{ background: isDark ? 'linear-gradient(135deg, #1A0050, #2E1065)' : 'linear-gradient(135deg, #f3f4f6, #e5e7eb)' }}
      >
        {/* Animated background dots */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }}
        />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 px-8">
          {/* Play button */}
          <div className="w-16 h-16 flex items-center justify-center bg-white/15 rounded-full border-2 border-white/30 shrink-0">
            <i className="ri-play-fill text-white text-3xl ml-1"></i>
          </div>
          <div className="text-center sm:text-left">
            <div className={`font-black text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>{title}</div>
            {subtitle && <div className={`text-sm mb-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{subtitle}</div>}
            <div className="flex flex-wrap items-center gap-3 justify-center sm:justify-start">
              {duration && (
                <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  <i className="ri-time-line"></i>{duration}
                </span>
              )}
              <span className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium ${isDark ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-amber-100 text-amber-700'}`}>
                <i className="ri-loader-4-line animate-spin"></i>
                视频制作中，即将上线
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Has src — real player
  return (
    <div
      ref={containerRef}
      className={`relative rounded-2xl overflow-hidden bg-black ${heightClass} ${className} group`}
      onMouseMove={showControlsTemporarily}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => { if (playing) setShowControls(false); }}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="w-full h-full object-contain"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => { setPlaying(false); setStarted(false); setProgress(0); setCurrentTime(0); }}
        onWaiting={() => setBuffering(true)}
        onCanPlay={() => setBuffering(false)}
        onClick={handlePlayPause}
        playsInline
      />

      {/* Big play button overlay (before start) */}
      {!started && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer bg-black/40"
          onClick={handlePlayPause}
        >
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full border-2 border-white/50 hover:bg-white/30 transition-all mb-4">
            <i className="ri-play-fill text-white text-4xl ml-1.5"></i>
          </div>
          <div className="text-white font-bold text-base text-center px-6">{title}</div>
          {duration && <div className="text-white/50 text-xs mt-1">{duration}</div>}
        </div>
      )}

      {/* Buffering spinner */}
      {buffering && started && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 flex items-center justify-center bg-black/50 rounded-full">
            <i className="ri-loader-4-line text-white text-2xl animate-spin"></i>
          </div>
        </div>
      )}

      {/* Controls bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 transition-all duration-300 ${showControls || !playing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)', paddingTop: '40px' }}
      >
        {/* Progress bar */}
        <div
          className="mx-3 mb-2 h-1.5 bg-white/20 rounded-full cursor-pointer relative group/bar"
          onClick={handleSeek}
        >
          <div
            className="h-full bg-[#F59E0B] rounded-full relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#F59E0B] rounded-full opacity-0 group-hover/bar:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Buttons row */}
        <div className="flex items-center gap-2 px-3 pb-3">
          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            className="w-8 h-8 flex items-center justify-center text-white hover:text-[#F59E0B] transition-colors cursor-pointer"
          >
            <i className={playing ? 'ri-pause-fill text-xl' : 'ri-play-fill text-xl ml-0.5'}></i>
          </button>

          {/* Time */}
          <span className="text-white/70 text-xs font-mono whitespace-nowrap">
            {formatTime(currentTime)} / {formatTime(totalDuration)}
          </span>

          <div className="flex-1" />

          {/* Volume */}
          <button
            onClick={toggleMute}
            className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className={muted || volume === 0 ? 'ri-volume-mute-line' : volume < 0.5 ? 'ri-volume-down-line' : 'ri-volume-up-line'}></i>
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={muted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-16 h-1 accent-[#F59E0B] cursor-pointer"
          />

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className={fullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}></i>
          </button>
        </div>
      </div>
    </div>
  );
}
